import React, { useEffect, useState } from "react";
import CurrencyConverter from "../CurrencyConverter";
import AuditNoteFields, {
  AuditNoteValues,
  EMPTY_AUDIT_NOTE,
  EMPTY_AUDIT_NOTE_ERRORS,
  hasAuditNoteError,
  trimAuditNote,
  validateAuditNote,
} from "../AuditNoteFields";
import CollapsibleSection from "../CollapsibleSection";
import BillingModalShell, {
  CurrencyBadge,
  ModalLoadingState,
} from "../BillingModalShell";
import {
  CompactSelect,
  Field,
  FieldGroup,
  PrimaryButton,
  SecondaryButton,
  SectionHeading,
  SelectInput,
  SummaryLine,
  TextInput,
} from "../BillingFormControls";
import "./style.css";

import { useParams } from "react-router-dom";
import { notify } from "../../../utils/utils";
import {
  CENTRALISED_BILLING_API,
  UNIFIED_DASHBOARD_URL,
} from "../../../utils/envConfig";
import { useSelector } from "react-redux";

const normalizeCurrency = (currency?: string | null) => {
  return currency ? currency.toLowerCase() : "";
};

const getRegisteredBillingCurrency = (
  subscription?: any,
  customPlanDetails?: any,
) => {
  return (
    normalizeCurrency(customPlanDetails?.currency) ||
    normalizeCurrency(subscription?.currency) ||
    normalizeCurrency(subscription?.plan?.currency) ||
    normalizeCurrency(subscription?.plan?.metadata?.currency) ||
    "usd"
  );
};

const getCurrencyChangeBlockedMessage = (
  registeredCurrency: string,
  attemptedCurrency: string,
) => {
  return `The workspace is registered in ${registeredCurrency.toUpperCase()} currency, can't move to ${attemptedCurrency.toUpperCase()} currency`;
};

const CREDIT_RATE_BY_CURRENCY: Record<string, number> = {
  usd: 0.05,
  inr: 3.1666666,
};

const formatCalculatedAmount = (amount: number) => {
  return amount.toFixed(2).replace(/\.?0+$/, "");
};

const calculateAmountFromCredits = (
  credits: string | number,
  currency: string,
) => {
  const parsedCredits = Number(credits);

  if (!credits || Number.isNaN(parsedCredits)) {
    return "";
  }

  return formatCalculatedAmount(
    parsedCredits * (CREDIT_RATE_BY_CURRENCY[currency] || 0),
  );
};

const calculateCreditsFromAmount = (
  amount: string | number,
  currency: string,
) => {
  const parsedAmount = Number(amount);
  const rate = CREDIT_RATE_BY_CURRENCY[currency] || 0;

  if (!amount || Number.isNaN(parsedAmount) || rate === 0) {
    return "";
  }

  return formatCalculatedAmount(parsedAmount / rate);
};

const getCreditRateLabel = (currency: string) => {
  return formatCalculatedAmount(CREDIT_RATE_BY_CURRENCY[currency] || 0);
};

export default function TopupModal({
  onClose,
  getCustomCreditInfoDetails,
  subscription,
  customPlanDetails,
}) {
  const params = useParams();

  const globalReducer = useSelector((state: any) => state.globalReducer);
  const requiredCurrency = getRegisteredBillingCurrency(
    subscription,
    customPlanDetails,
  );

  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [credits, setCredits] = useState("");
  const [creditsError, setCreditsError] = useState("");
  const [currency, setCurrency] = useState(requiredCurrency);
  const [currencyError, setCurrencyError] = useState("");
  const [calculatorCurrency, setCalculatorCurrency] = useState(requiredCurrency);
  const [calculatorCredits, setCalculatorCredits] = useState("");
  const [calculatorAmount, setCalculatorAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const [unitAmount, setUnitAmount] = useState("1");
  const [unitAmountError, setUnitAmountError] = useState("");
  const [itemCount, setItemCount] = useState("1");
  const [itemCountError, setItemCountError] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [serviceNameError, setServiceNameError] = useState("");
  const [serviceNameList, setServiceNameList] = useState<string[]>([]);

  const [auditNote, setAuditNote] =
    useState<AuditNoteValues>(EMPTY_AUDIT_NOTE);
  const [auditNoteErrors, setAuditNoteErrors] = useState(
    EMPTY_AUDIT_NOTE_ERRORS,
  );

  useEffect(() => {
    fetchServiceName();
  }, []);

  useEffect(() => {
    setCurrency(requiredCurrency);
    setCalculatorCurrency(requiredCurrency);
    setCurrencyError("");
    setAmount(calculateAmountFromCredits(credits, requiredCurrency));
  }, [requiredCurrency]);

  const totalAmount = Number(amount || 0) * Number(itemCount || 0);

  const sanitizeNumberInput = (
    value: string,
    { allowDecimal = false }: { allowDecimal?: boolean } = {},
  ) => {
    if (value === "") {
      return "";
    }

    const numberPattern = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
    return numberPattern.test(value) ? value : null;
  };

  async function fetchServiceName() {
    try {
      const res = await fetch(`${CENTRALISED_BILLING_API}/external-services`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${globalReducer.userData.token}`,
        },
      }).then((res) => res.json());

      if (res?.error) {
        notify(res?.error || "Error fetching workspace", "error");
        return;
      }

      const services = res?.services?.map((s) => s.name) ?? [];
      setServiceNameList(services);
      setServiceName(services[0] ?? "catalogix");
    } catch (error) {
      console.log("Error fetching workspace", error);
      notify("Error fetching workspace", "error");
    }
  }

  const handleAddTopUpCredits = async () => {
    let isValid = true;

    const noteErrors = validateAuditNote(auditNote);
    setAuditNoteErrors(noteErrors);
    if (hasAuditNoteError(noteErrors)) {
      notify(noteErrors.note || noteErrors.requestedBy, "error");
      return;
    }

    const parsedCredits = Number(credits);
    const parsedAmount = Number(amount);
    const parsedItemCount = Number(itemCount);

    if (!currency) {
      setCurrencyError("Currency is required");
      isValid = false;
    } else if (currency !== requiredCurrency) {
      const currencyRestrictionMessage = getCurrencyChangeBlockedMessage(
        requiredCurrency,
        currency,
      );
      setCurrencyError(currencyRestrictionMessage);
      notify(currencyRestrictionMessage, "error");
      isValid = false;
      return;
    } else {
      setCurrencyError("");
    }

    if (!credits || Number.isNaN(parsedCredits) || parsedCredits <= 0) {
      setCreditsError("Credits must be greater than 0");
      isValid = false;
    } else {
      setCreditsError("");
    }

    if (amount === "" || Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setAmountError("Unit amount must be 0 or greater");
      isValid = false;
    } else {
      setAmountError("");
    }

    if (!itemCount || Number.isNaN(parsedItemCount) || parsedItemCount <= 0) {
      setItemCountError("Item Count must be greater than 0");
      isValid = false;
    } else {
      setItemCountError("");
    }

    if (!params.workspaceId) {
      notify("Missing workspaceId", "error");
      return;
    }

    if (isValid) {
      const trimmedNote = trimAuditNote(auditNote);
      const bodyData = {
        serviceName: serviceName,

        workspaceId: params.workspaceId,
        amount: credits,
        triggeredByUserId: globalReducer.userData._id,
        unitAmount: amount,
        itemCount: itemCount,
        currency,
        note: trimmedNote.note,
        requestedBy: trimmedNote.requestedBy,
        meta: {
          source: "admin_panel",
          info: "Manual top-up",
          isTopUp: true,
        },
      };

      setLoading(true);

      try {
        const topupRes = await fetch(
          `${CENTRALISED_BILLING_API}/custom/add-topup/${params.workspaceId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(bodyData),
          },
        ).then((res) => res.json());

        if (topupRes?.error) {
          notify(topupRes?.error || "Error adding plan", "error");
          setLoading(false);
          onClose();
          return;
        }
        onClose();
        setLoading(false);
        getCustomCreditInfoDetails();

        console.log("topupRes", topupRes);
      } catch (error) {
        notify("Error adding custom plan", "error");
        setLoading(false);
        onClose();
        console.log("Error adding custom plan", error);
      }
    }
  };

  if (loading) {
    return <ModalLoadingState message="Adding top-up credits..." />;
  }

  const currencySymbol = currency === "inr" ? "₹" : "$";

  const submit = () => {
    const noteErrors = validateAuditNote(auditNote);
    setAuditNoteErrors(noteErrors);
    if (hasAuditNoteError(noteErrors)) {
      notify(noteErrors.note || noteErrors.requestedBy, "error");
      return;
    }

    const c = confirm(
      `Are you sure you want to add, ${
        credits || 0
      } credits for amount ${currency.toUpperCase()} ${totalAmount}`,
    );
    if (!c) {
      return;
    }

    handleAddTopUpCredits();
  };

  return (
    <BillingModalShell
      title="Top up credits"
      badge={<CurrencyBadge currency={currency} />}
      subtitle="One-off credits added on top of the current plan. The plan itself is unchanged."
      onClose={onClose}
      footer={
        <div className="flex flex-row flex-wrap items-center justify-between gap-3">
          <SummaryLine
            items={[
              { label: "Adding", value: `${credits || 0} credits` },
              {
                label: "Total",
                value: `${currencySymbol}${totalAmount}`,
              },
            ]}
          />
          <div className="flex flex-row items-center gap-2">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton onClick={submit}>Add credits</PrimaryButton>
          </div>
        </div>
      }
    >
      <section>
        <SectionHeading
          title="Top-up"
          hint={`1 credit = ${currency.toUpperCase()} ${getCreditRateLabel(
            currency,
          )}`}
        />
        <FieldGroup>
          <Field
            label="Credits"
            required
            error={creditsError}
          >
            <TextInput
              value={credits}
              onChange={(value) => {
                const sanitizedValue = sanitizeNumberInput(value);

                if (sanitizedValue === null) {
                  return;
                }

                setCredits(sanitizedValue);
                setAmount(
                  calculateAmountFromCredits(sanitizedValue, currency),
                );
                setCalculatorCredits(sanitizedValue);
                setCalculatorAmount(
                  calculateAmountFromCredits(
                    sanitizedValue,
                    calculatorCurrency,
                  ),
                );
                if (creditsError) {
                  setCreditsError("");
                }
              }}
              placeholder="10000"
              inputMode="numeric"
              error={creditsError}
            />
          </Field>

          <Field
            label="Unit amount"
            required
            error={amountError}
            help="Auto-filled from credits. Override if the deal differs."
          >
            <TextInput
              value={amount}
              onChange={(value) => {
                const sanitizedValue = sanitizeNumberInput(value, {
                  allowDecimal: true,
                });

                if (sanitizedValue === null) {
                  return;
                }

                setAmount(sanitizedValue);
                if (amountError) {
                  setAmountError("");
                }
              }}
              placeholder="1000"
              inputMode="decimal"
              prefix={currencySymbol}
              error={amountError}
            />
          </Field>

          <Field
            label="Currency"
            required
            error={currencyError}
            help={`Locked to ${requiredCurrency.toUpperCase()} for this workspace.`}
          >
            <SelectInput
              value={currency}
              onChange={(selectedCurrency) => {
                if (selectedCurrency !== requiredCurrency) {
                  const currencyRestrictionMessage =
                    getCurrencyChangeBlockedMessage(
                      requiredCurrency,
                      selectedCurrency,
                    );
                  notify(currencyRestrictionMessage, "error");
                  setCurrencyError(currencyRestrictionMessage);
                  setCurrency(requiredCurrency);
                  return;
                }

                setCurrency(selectedCurrency);
                setAmount(
                  calculateAmountFromCredits(credits, selectedCurrency),
                );
                setCurrencyError("");
              }}
              options={[
                { value: "usd", label: "USD" },
                { value: "inr", label: "INR" },
              ]}
            />
          </Field>
        </FieldGroup>
      </section>

      <AuditNoteFields
        values={auditNote}
        errors={auditNoteErrors}
        onChange={(next) => {
          setAuditNote(next);
          setAuditNoteErrors(EMPTY_AUDIT_NOTE_ERRORS);
        }}
        notePlaceholder="e.g. pre-paid top-up agreed with the customer"
      />

      <section>
        <SectionHeading title="Reference" hint="Optional tools" />
        <div className="flex flex-col gap-3">
          <CollapsibleSection
            title="Credit calculator"
            subtitle={`1 credit = ${calculatorCurrency.toUpperCase()} ${getCreditRateLabel(
              calculatorCurrency,
            )}`}
            headerRight={
              <CompactSelect
                value={calculatorCurrency}
                ariaLabel="Calculator currency"
                onChange={(nextCurrency) => {
                  setCalculatorCurrency(nextCurrency);
                  setCalculatorAmount(
                    calculateAmountFromCredits(
                      calculatorCredits,
                      nextCurrency,
                    ),
                  );
                }}
                options={[
                  { value: "usd", label: "USD" },
                  { value: "inr", label: "INR" },
                ]}
              />
            }
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Credits">
                <TextInput
                  value={calculatorCredits}
                  onChange={(nextCredits) => {
                    if (
                      nextCredits !== "" &&
                      !/^\d*\.?\d*$/.test(nextCredits)
                    ) {
                      return;
                    }

                    setCalculatorCredits(nextCredits);
                    setCalculatorAmount(
                      calculateAmountFromCredits(
                        nextCredits,
                        calculatorCurrency,
                      ),
                    );
                  }}
                  placeholder="Enter credits"
                  inputMode="decimal"
                />
              </Field>
              <Field label="Amount">
                <TextInput
                  value={calculatorAmount}
                  onChange={(nextAmount) => {
                    if (
                      nextAmount !== "" &&
                      !/^\d*\.?\d*$/.test(nextAmount)
                    ) {
                      return;
                    }

                    setCalculatorAmount(nextAmount);
                    setCalculatorCredits(
                      calculateCreditsFromAmount(
                        nextAmount,
                        calculatorCurrency,
                      ),
                    );
                  }}
                  placeholder="Enter amount"
                  inputMode="decimal"
                  prefix={calculatorCurrency === "inr" ? "₹" : "$"}
                />
              </Field>
            </div>
            <div className="mt-3 rounded-xl bg-[#f2eff3] px-3 py-2 font-['Geist:Medium',_sans-serif] text-[13px] font-medium leading-[20px] text-[#211f26]">
              {calculatorCredits || 0} credits ={" "}
              {calculatorCurrency.toUpperCase()} {calculatorAmount || 0}
            </div>
          </CollapsibleSection>

          <CurrencyConverter
            amount={amount}
            baseCurrency={currency}
            collapsible
          />
        </div>
      </section>
    </BillingModalShell>
  );
}
